<template>
  <!--
    Appliance and switch illustrations — one *glass* silhouette per load, one
    moving part, and colour only where it says something: the medium (water,
    coffee, lamplight, current) in its own hue, and the LED in the tile's.

    The glass is fixed and light (a pale body lifted by the ink it is drawn in),
    so a machine reads as a machine on a white card instead of turning into a
    black slab: `currentColor` is the tile's state colour, and it tints the body
    faintly. Frames, seams and handles are the edge and line tones, never the
    state colour, so switching a load on lights the marks rather than repainting
    the appliance.
  -->
  <svg
    class="switch-anim"
    :class="[`switch-anim--${kind}`, { 'switch-anim--active': active }]"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <defs>
      <radialGradient :id="glowId" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stop-color="#fde68a" stop-opacity="0.95" />
        <stop offset="50%" stop-color="#fbbf24" stop-opacity="0.38" />
        <stop offset="100%" stop-color="#f59e0b" stop-opacity="0" />
      </radialGradient>
    </defs>

    <!-- ── Wall socket, EU (Schuko): the plate, the round recess with its rim,
         the two round pin holes and the earthing contacts top and bottom. ── -->
    <g v-if="kind === 'socket'">
      <rect class="app__glass" x="9.5" y="6.6" width="29" height="34.8" rx="6.6" />
      <circle class="app__panel" cx="24" cy="21.8" r="11.8" />
      <circle class="app__rim" cx="24" cy="21.8" r="11.8" />
      <circle class="app__hole" cx="19.9" cy="21.8" r="2.5" />
      <circle class="app__hole" cx="28.1" cy="21.8" r="2.5" />
      <circle class="app__clip" cx="24" cy="11.6" r="1.1" />
      <circle class="app__clip" cx="24" cy="32" r="1.1" />
      <circle class="app__led" cx="24" cy="37" r="1.7" />
    </g>

    <!-- ── Coffee machine: body, group head, cup. Live: the spout drips and the
         cup steams. ─────────────────────────────────────────────────────── -->
    <g v-else-if="kind === 'coffee'">
      <rect class="app__glass" x="10" y="7.4" width="28" height="16.4" rx="3.2" />
      <rect class="app__panel" x="13.4" y="10.8" width="8" height="3.4" rx="1.2" />
      <rect class="app__panel" x="23.6" y="19" width="10.4" height="3.4" rx="1.4" />
      <path class="app__line" d="M28.8,22.4 V25.2" />
      <path class="app__glass" d="M22.8,27 h12 v5.2 a4.2,4.2 0 0 1 -4.2,4.2 h-3.6 a4.2,4.2 0 0 1 -4.2,-4.2 Z" />
      <path class="app__line" d="M34.8,28.4 h1.6 a2.6,2.6 0 0 1 0,5.2 h-1.6" />
      <path
        class="app__live-marks app__brew"
        d="M23.8,30.4 h10 v1.4 a3.4,3.4 0 0 1 -3.4,3.4 h-3.2 a3.4,3.4 0 0 1 -3.4,-3.4 Z" />
      <circle class="app__led" cx="13" cy="22.4" r="1.4" />
      <g class="app__live-marks">
        <circle class="app__drop" cx="28.8" cy="25.8" r="1.1" />
        <path class="app__steam" d="M26.6,26.6 c0,-1.8 1.5,-1.8 1.5,-3.6" />
        <path class="app__steam app__steam--b" d="M31,26.6 c0,-1.8 1.5,-1.8 1.5,-3.6" />
      </g>
    </g>

    <!-- ── Washing machine / dryer: the drum behind its round door. Live: the
         drum turns, and a washer shows water in it. ─────────────────────── -->
    <g v-else-if="kind === 'washer' || kind === 'dryer'">
      <rect class="app__glass" x="9" y="7" width="30" height="34" rx="4.2" />
      <rect class="app__panel" x="12.2" y="10.4" width="9" height="3.8" rx="1.4" />
      <circle class="app__knob" cx="33.4" cy="12.3" r="2" />
      <circle class="app__panel" cx="24" cy="26.4" r="10.6" />
      <g class="app__drum">
        <circle class="app__line" cx="24" cy="26.4" r="7.2" />
        <path class="app__line" d="M24,19.2 V22.6" />
        <path class="app__line" d="M30.4,30.2 L27.4,28.4" />
        <path class="app__line" d="M17.6,30.2 L20.6,28.4" />
      </g>
      <path v-if="kind === 'washer'" class="app__live-marks app__fill" d="M17.6,29.6 a6.4,6.4 0 0 0 12.8,0 Z" />
      <g v-else class="app__live-marks app__heat">
        <path d="M19.6,29.4 c0,-1.5 1.5,-1.5 1.5,-3" />
        <path d="M24,29.4 c0,-1.5 1.5,-1.5 1.5,-3" />
        <path d="M28.4,29.4 c0,-1.5 1.5,-1.5 1.5,-3" />
      </g>
      <circle class="app__led" cx="33.4" cy="18" r="1.4" />
    </g>

    <!-- ── Dishwasher: the machine with its door down, the plates on the rack,
         and the spray going through them. No window — the plates ARE what it
         shows, and the water is what says it is running. ─────────────────── -->
    <g v-else-if="kind === 'dishwasher'">
      <rect class="app__glass" x="8" y="8.4" width="32" height="31.2" rx="4.2" />
      <path class="app__line" d="M12,15 H36" />
      <circle class="app__knob" cx="14.6" cy="11.6" r="1.5" />
      <path class="app__line" d="M19,11.6 H33" />
      <path class="app__line" d="M14,31 H34" />
      <path class="app__line" d="M15.6,30.6 a3,3 0 0 1 6,0" />
      <path class="app__line" d="M21,30.6 a3,3 0 0 1 6,0" />
      <path class="app__line" d="M26.4,30.6 a3,3 0 0 1 6,0" />
      <path class="app__line" d="M12,35.4 H36" />
      <path class="app__line" d="M18,37.6 H30" />
      <g class="app__live-marks app__jet">
        <path d="M13.4,19.8 c3.2,2.4 6.4,-2.4 9.6,0 c3.2,2.4 6.4,-2.4 9.6,0 c1.6,1.2 3,1.2 4.4,0" />
        <path class="app__jet--b" d="M13.4,24 c3.2,2.4 6.4,-2.4 9.6,0 c3.2,2.4 6.4,-2.4 9.6,0 c1.6,1.2 3,1.2 4.4,0" />
      </g>
      <circle class="app__led" cx="33.4" cy="37.6" r="1.2" />
    </g>

    <!-- ── Fridge / freezer: two doors, one seam, and the cold air inside. ─── -->
    <g v-else-if="kind === 'fridge'">
      <rect class="app__glass" x="14" y="7" width="20" height="34" rx="3.6" />
      <rect class="app__tint" x="15.2" y="8.2" width="17.6" height="10.4" rx="2.2" />
      <rect class="app__tint" x="15.2" y="21.6" width="17.6" height="18.2" rx="2.2" />
      <path class="app__line" d="M14,20.4 H34" />
      <rect class="app__knob" x="29.8" y="11.4" width="2" height="5.2" rx="1" />
      <rect class="app__knob" x="29.8" y="23.8" width="2" height="5.2" rx="1" />
      <g class="app__live-marks app__cold">
        <path class="app__cold-air" d="M16.8,24 h4.4 l-2.2,3.4 Z" />
        <path class="app__cold-air app__cold-air--b" d="M21.2,27.4 h4.4 l-2.2,3.4 Z" />
        <path class="app__cold-air app__cold-air--c" d="M25.2,23.6 h4.4 l-2.2,3.4 Z" />
      </g>
      <circle class="app__led" cx="24" cy="15" r="1.3" />
    </g>

    <!-- ── Oven: hobs and knobs above, the element in the window below. ───── -->
    <g v-else-if="kind === 'oven'">
      <rect class="app__glass" x="9" y="7.4" width="30" height="33.2" rx="4.2" />
      <path class="app__line" d="M12,14.6 H36" />
      <circle class="app__knob" cx="15.2" cy="11" r="1.6" />
      <circle class="app__knob" cx="20.6" cy="11" r="1.6" />
      <rect class="app__panel" x="12.4" y="18.2" width="23.2" height="15.6" rx="2.4" />
      <path class="app__live-marks app__element" d="M16.6,30.4 H31.4" />
      <path class="app__line" d="M17.4,36.8 H30.6" />
      <circle class="app__led" cx="33.6" cy="11" r="1.3" />
    </g>

    <!-- ── Kettle: a tapered jug, its lid, the handle and the spout — and the
         water it is heating. ─────────────────────────────────────────────── -->
    <g v-else-if="kind === 'kettle'">
      <path class="app__glass" d="M16.6,17.4 h13.2 v14.4 a4.6,4.6 0 0 1 -4.6,4.6 h-4 a4.6,4.6 0 0 1 -4.6,-4.6 Z" />
      <path class="app__panel" d="M16,17.4 h14.4 l-1.6,-3.6 h-11.2 Z" />
      <path class="app__line" d="M30,21.6 h1.8 a4.6,4.6 0 0 1 0,9.2 H30" />
      <path class="app__panel" d="M16.6,20.4 L11.4,17 a4.2,4.2 0 0 0 0,7.8 L16.6,22.6 Z" />
      <path
        class="app__live-marks app__fill"
        d="M18,24.6 h10.6 v7.2 a3.4,3.4 0 0 1 -3.4,3.4 h-3.8 a3.4,3.4 0 0 1 -3.4,-3.4 Z" />
      <g class="app__live-marks">
        <path class="app__steam" d="M12,15.6 c0,-2 1.6,-2 1.6,-4" />
        <path class="app__steam app__steam--b" d="M15.8,15.6 c0,-2 1.6,-2 1.6,-4" />
      </g>
    </g>

    <!-- ── Dimmable plug / lamp: the shade takes the level, in lamplight. ─── -->
    <g v-else-if="kind === 'dimmer'">
      <path class="app__glass" d="M16.2,20 L19.6,10.2 h8.8 L31.8,20 Z" />
      <path class="app__lit-shade" d="M16.2,20 L19.6,10.2 h8.8 L31.8,20 Z" :style="{ opacity: glowOpacity }" />
      <circle class="app__glow" cx="24" cy="20.6" r="9.6" :fill="`url(#${glowId})`" :style="{ opacity: glowOpacity }" />
      <path class="app__line" d="M24,20 V32.4" />
      <rect class="app__glass" x="17.4" y="32.4" width="13.2" height="4.8" rx="2.2" />
      <circle class="app__led" cx="24" cy="34.8" r="1.2" />
    </g>

    <!-- ── EV charger: the wallbox, the cable, and the car on the other end.
         Live, the current runs down the cable from the box to the port. ──── -->
    <g v-else-if="kind === 'ev'">
      <rect class="app__glass" x="5.8" y="10.4" width="11.4" height="20.4" rx="3.2" />
      <rect class="app__panel" x="7.8" y="12.6" width="7.4" height="6" rx="1.4" />
      <path class="app__bolt" d="M12.2,13.4 L9.6,16.6 h1.9 l-0.8,2.4 3.2,-3.6 h-1.9 Z" />
      <circle class="app__led" cx="11.5" cy="27.4" r="1.2" />
      <path class="app__line" d="M17.2,23.4 c4.2,0 3,5.4 8.2,5.4" />
      <path class="app__live-marks app__current" d="M17.2,23.4 c4.2,0 3,5.4 8.2,5.4" />
      <path class="app__glass" d="M25.4,32.6 v-6.4 l3,-1 2.6,-4.2 h8 l3,4.2 3.4,1 v6.4 Z" />
      <path class="app__panel" d="M29.8,21.4 h7.4 l2.4,3.2 h-12.2 Z" />
      <circle class="app__knob" cx="29.4" cy="32.8" r="2.6" />
      <circle class="app__knob" cx="39.6" cy="32.8" r="2.6" />
    </g>

    <!-- ── Robot vacuum: a disc seen from above, working the floor. ───────── -->
    <g v-else-if="kind === 'vacuum'">
      <g class="app__roam">
        <circle class="app__glass" cx="24" cy="24.6" r="13" />
        <circle class="app__panel" cx="24" cy="24.6" r="8.4" />
        <path class="app__line" d="M11.6,19.4 a13,13 0 0 1 24.8,0" />
        <circle class="app__knob" cx="24" cy="24.6" r="2.4" />
      </g>
      <g class="app__live-marks app__dust">
        <circle cx="39.6" cy="36" r="1.2" />
        <circle cx="42.4" cy="32" r="0.9" />
        <circle cx="44.6" cy="37.4" r="0.7" />
      </g>
    </g>

    <!-- ── Robot mower: a low deck, the wheels at its corners, the blade in
         the cut, and the grass it leaves behind. ─────────────────────────── -->
    <g v-else-if="kind === 'mower'">
      <path class="app__glass" d="M8.6,29.4 v-5.6 a4.6,4.6 0 0 1 4.6,-4.6 h21.6 a4.6,4.6 0 0 1 4.6,4.6 v5.6 Z" />
      <path class="app__line" d="M8.6,25.6 h30.8" />
      <rect class="app__panel" x="18.4" y="17.4" width="11.2" height="3.4" rx="1.4" />
      <circle class="app__knob" cx="24" cy="23" r="1.5" />
      <rect class="app__knob" x="9.6" y="26.6" width="5.8" height="6.6" rx="2.7" />
      <rect class="app__knob" x="32.6" y="26.6" width="5.8" height="6.6" rx="2.7" />
      <g class="app__blade app__mower-blade">
        <circle class="app__panel" cx="24" cy="33" r="4" />
        <path class="app__line" d="M20,33 H28 M24,29 V37" />
      </g>
      <g class="app__grass">
        <path d="M12,41.4 c0,-1.6 1.2,-1.6 1.2,-3.2" />
        <path d="M16.4,41.4 c0,-1.2 1,-1.2 1,-2.6" />
        <path d="M31.6,41.4 c0,-1.2 1,-1.2 1,-2.6" />
        <path d="M35.4,41.4 c0,-1.6 1.2,-1.6 1.2,-3.2" />
      </g>
      <g class="app__live-marks app__clippings">
        <path d="M18,36.6 c0,-1.4 1.2,-1.4 1.2,-2.8" />
        <path d="M30.4,36.6 c0,-1.4 1.2,-1.4 1.2,-2.8" />
        <path d="M24,38.6 c0,-1.4 1.2,-1.4 1.2,-2.8" />
      </g>
    </g>

    <!-- ── Siren: the bell, its clapper, and the sound coming off it. ─────── -->
    <g v-else-if="kind === 'siren'">
      <g class="app__horn">
        <path class="app__glass" d="M12.6,28.4 a11.4,11.4 0 0 1 22.8,0 Z" />
        <path class="app__line" d="M11.4,28.4 H36.6" />
      </g>
      <g class="app__live-marks app__sound">
        <path d="M9.6,20.4 c-2.6,2.8 -2.6,8.8 0,11.6" />
        <path class="app__sound--b" d="M36.4,20.4 c2.6,2.8 2.6,8.8 0,11.6" />
        <path class="app__sound--c" d="M6.2,17.2 c-3.8,4 -3.8,13.6 0,17.6" />
        <path class="app__sound--d" d="M39.8,17.2 c3.8,4 3.8,13.6 0,17.6" />
      </g>
      <path class="app__line" d="M24,28.4 V32.4" />
      <rect class="app__panel" x="10.6" y="32.4" width="26.8" height="5.4" rx="2.2" />
      <circle class="app__knob" cx="24" cy="28.8" r="1.8" />
      <circle class="app__led" cx="13.8" cy="35.1" r="1.2" />
    </g>

    <!-- ── Doorbell: the plate, its grille, the lens, the button under it. ── -->
    <g v-else-if="kind === 'bell'">
      <rect class="app__glass" x="13.6" y="7.4" width="20.8" height="33.2" rx="6" />
      <g class="app__line">
        <path d="M20.4,12.4 h7.2" />
        <path d="M20.4,15.2 h7.2" />
      </g>
      <circle class="app__knob" cx="24" cy="20.4" r="1.7" />
      <circle class="app__panel" cx="24" cy="30.2" r="6.4" />
      <circle class="app__line" cx="24" cy="30.2" r="2.6" />
      <circle class="app__live-marks app__ripple" cx="24" cy="30.2" :r="rippleR" />
    </g>

    <!-- ── Pump: the casing, the impeller, the discharge it pushes. ───────── -->
    <g v-else-if="kind === 'pump'">
      <circle class="app__glass" cx="20" cy="25.4" r="11.2" />
      <circle class="app__panel" cx="20" cy="25.4" r="7.4" />
      <g class="app__impeller">
        <path class="app__line" d="M20,19.4 V31.4 M14,25.4 H26 M15.8,21.2 L24.2,29.6 M24.2,21.2 L15.8,29.6" />
      </g>
      <rect class="app__glass" x="9" y="11.4" width="22" height="5.6" rx="2.2" />
      <path class="app__line" d="M31,25.4 H39" />
      <g class="app__live-marks app__flow">
        <path d="M33.4,23.6 l1.8,1.8 -1.8,1.8" />
        <path class="app__flow--b" d="M37.4,23.6 l1.8,1.8 -1.8,1.8" />
      </g>
    </g>

    <!-- ── Water valve: a quarter-turn fitting in a riser ───────────────────
         A quarter-turn valve in a riser: the pipe runs straight through from
         the floor to the top, and the red lever is bolted to the body's own
         axis — reaching across the valve while it is shut, and down along the
         pipe while it is open. Open, the fitting breathes, because a valve left
         open is a warning. -->
    <g v-else-if="kind === 'valve'" class="switch-anim__valve">
      <!-- The riser: a slim pipe with its two walls drawn on top of it, running
           through the fitting and out the other side -->
      <path class="switch-anim__valve-pipe" d="M24,48 V0" />
      <!-- Water sits inside that pipe while the valve is open: a column in the
           bore, and its own current running up the riser (drawn under the
           fitting, so the body still reads as metal) -->
      <path class="switch-anim__valve-water" d="M24,48 V0" />
      <path class="switch-anim__valve-flow" d="M24,48 V0" />
      <path class="switch-anim__valve-wall" d="M20.3,48 V33.4" />
      <path class="switch-anim__valve-wall" d="M27.7,48 V33.4" />
      <path class="switch-anim__valve-wall" d="M20.3,18.4 V0" />
      <path class="switch-anim__valve-wall" d="M27.7,18.4 V0" />

      <!-- The fitting: a flange at each end of the body, which carries the
           lever on its centre line -->
      <rect class="switch-anim__valve-collar" x="16.2" y="30.6" width="15.6" height="3.2" rx="1.2" />
      <rect class="switch-anim__valve-collar" x="16.2" y="18.2" width="15.6" height="3.2" rx="1.2" />
      <rect class="switch-anim__valve-body" x="17.4" y="21.4" width="13.2" height="9.2" rx="2.4" />
      <path class="switch-anim__valve-seam" d="M18.4,23.8 H29.6" />

      <!-- The lever: hub on the axis, grip reaching out, short tail opposite.
           It is the one coloured part, and the only one that moves. -->
      <g class="switch-anim__valve-handle">
        <rect class="switch-anim__valve-grip" x="23" y="24" width="18.5" height="4" rx="2" />
        <rect class="switch-anim__valve-grip" x="18.6" y="24.5" width="4.4" height="3" rx="1.5" />
        <circle class="switch-anim__valve-hub" cx="24" cy="26" r="2.6" />
      </g>
    </g>

    <!-- ── Anything else: the plain rocker switch ─────────────────────────── -->
    <g v-else>
      <rect class="app__glass" x="11" y="9" width="26" height="30" rx="5.4" />
      <rect class="app__rocker" x="17" :y="rockerY" width="14" height="14" rx="3" />
      <circle class="app__live-marks app__led-ring" cx="24" cy="24" :r="liveR" />
    </g>
  </svg>
</template>

<script lang="ts">
/** Module scope: gradient ids must be unique per instance. */
let instanceSeq = 0;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { SwitchKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: SwitchKind;
    /** The load is live — this is the only thing the movement is keyed off. */
    active?: boolean;
    /** 0–100 for a dimmable plug: scales the glow, does not gate it. */
    level?: number | null;
  }>(),
  { kind: 'socket', active: false, level: null },
);

/** Ids must be unique per instance, or the first gradient wins for every tile. */
const uid = `switch-anim-${++instanceSeq}`;
const glowId = `${uid}-glow`;

/** The plain rocker's ring breathes rather than sizes — r stays put. */
const liveR = computed(() => 13);

/** A dimmable plug's bulb takes its level; an unreadable level is half. */
const glowOpacity = computed(() => {
  const pct =
    typeof props.level === 'number' && Number.isFinite(props.level) ? Math.min(100, Math.max(0, props.level)) : 50;
  return props.active ? 0.25 + 0.6 * (pct / 100) : 0;
});

/** The plain rocker rests up and sits down when the load is live. */
const rockerY = computed(() => (props.active ? 21 : 17));

/** The doorbell's ripple leaves the button while the bell rings. */
const rippleR = computed(() => (props.kind === 'bell' ? 6 : 3));
</script>

<style scoped>
.switch-anim {
  display: block;
  width: 100%;
  height: 100%;

  /* The appliance palette. Glass is fixed and light — the ink only lifts it —
     so a machine stays a machine on a white card. `currentColor` is the tile's
     state colour, and it appears at full strength only on the LED. */
  --app-glass: color-mix(in srgb, currentColor 11%, rgb(234 239 245));
  --app-panel: color-mix(in srgb, currentColor 17%, rgb(206 217 229));
  --app-edge: color-mix(in srgb, currentColor 26%, rgb(150 165 184));
  --app-line: color-mix(in srgb, currentColor 22%, rgb(120 136 156));
  --app-ink: color-mix(in srgb, currentColor 55%, rgb(71 85 105));

  /* The medium keeps its own colour: water is blue on any tile, coffee is
     coffee, lamplight is warm. Only the LED speaks for the switch's state. */
  --app-water: #38bdf8;
  --app-cold: #7dd3fc;
  --app-brew: #b06a3b;
  --app-lamp: #fbbf24;
  --app-charge: #34d399;
  --app-heat: #f97316;
  --app-grass: #4ade80;
}

/* ── The three marks every appliance is drawn with ──────────────────────── */
.app__glass {
  fill: var(--app-glass);
  stroke: var(--app-edge);
  stroke-width: 1.4;
  stroke-linejoin: round;
}

.app__panel {
  fill: var(--app-panel);
  stroke: var(--app-edge);
  stroke-width: 1.1;
  stroke-linejoin: round;
}

.app__line {
  fill: none;
  stroke: var(--app-line);
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.app__knob,
.app__hole,
.app__rocker {
  fill: var(--app-ink);
}

.app__clip {
  fill: var(--app-ink);
  opacity: 0.7;
}

/* The recess rim: a hairline inside the plate, so the socket reads as a
   recessed fitting rather than a drawn ring. */
.app__rim {
  fill: none;
  stroke: var(--app-edge);
  stroke-width: 1;
}

/* ── What the load's own colour says ─────────────────────────────────────
   The LED takes the tile's state colour; the mediums take theirs. Nothing else
   in the drawing is coloured, so a live tile reads at a glance. */
.app__led,
.app__tint,
.app__fill,
.app__element,
.app__glow {
  opacity: 0;
  transition: opacity 0.35s ease;
}

.switch-anim--active .app__led,
.switch-anim--active .app__tint,
.switch-anim--active .app__fill,
.switch-anim--active .app__element,
.switch-anim--active .app__glow {
  opacity: 1;
}

.app__led {
  fill: currentColor;
}

.app__tint {
  fill: var(--app-cold);
  fill-opacity: 0.3;
  stroke: none;
}

.app__fill {
  fill: var(--app-water);
  fill-opacity: 0.55;
  stroke: none;
}

.app__element {
  fill: none;
  stroke: var(--app-heat);
  stroke-width: 2.6;
  stroke-linecap: round;
}

.app__glow {
  stroke: none;
}

/* The lamp's shade, tinted by the level it is set to — a dim bulb is a pale
   shade, a bright one is lamplight. */
.app__lit-shade {
  fill: var(--app-lamp);
  fill-opacity: 0.62;
  stroke: none;
  transition: none;
}

/* Coffee in the cup, and the drip that fills it. */
.app__brew,
.app__drop {
  fill: var(--app-brew);
}

/* Cold air: the tint of an interior held at temperature, and the air moving
   down through it — darker than the tint it sits on, or it disappears. */
.app__cold-air {
  fill: color-mix(in srgb, var(--app-cold) 45%, #0284c7);
}

/* The charger's current, running down the cable. */
.app__current {
  fill: none;
  stroke: var(--app-charge);
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-dasharray: 2.6 5;
}

/* The bolt in the wallbox display: the one always-lit mark, because a charger
   says what it is before it is switched on. */
.app__bolt {
  fill: var(--app-charge);
}

/* The grass the mower has yet to cut. */
.app__grass {
  fill: none;
  stroke: var(--app-grass);
  stroke-width: 1.4;
  stroke-linecap: round;
  opacity: 0.6;
}

/* A live mark that is a group rather than a shape: hidden until the load runs */
.app__live-marks {
  opacity: 0;
  transition: opacity 0.35s ease;
}

.switch-anim--active .app__live-marks {
  opacity: 1;
}

.app__steam {
  fill: none;
  stroke: color-mix(in srgb, var(--app-water) 22%, #94a3b8);
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-opacity: 0.8;
}

/* Sound, hot air and clippings are lines, not shapes — an open path filled
   black would read as a smudge. */
.app__sound path,
.app__heat path,
.app__clippings path {
  fill: none;
  stroke-width: 1.4;
  stroke-linecap: round;
}

.app__sound path {
  stroke: var(--app-heat);
  stroke-width: 1.8;
  stroke-opacity: 0.9;
}

.app__heat path {
  stroke: var(--app-heat);
  stroke-opacity: 0.8;
}

.app__clippings path {
  stroke: var(--app-grass);
}

.app__flow path {
  fill: var(--app-water);
  stroke: none;
}

.app__dust circle {
  fill: var(--app-ink);
}

.app__fill,
.app__ripple {
  transform-box: fill-box;
}

.app__ripple {
  fill: none;
  stroke: var(--app-lamp);
  stroke-width: 1.4;
  transform-origin: center;
}

/* ── The dishwasher's water: a jet running through the plates, dashed so the
      movement is the water itself — a still line would read as a rod. ───── */
.app__jet {
  fill: none;
  stroke: var(--app-water);
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-dasharray: 4 3.4;
}

.switch-anim--active .app__jet path {
  animation: switch-anim-jet 1.1s linear infinite;
}

.switch-anim--active .app__jet--b {
  animation-delay: 0.35s;
}

@keyframes switch-anim-jet {
  to {
    stroke-dashoffset: -14.8;
  }
}

/* The plain rocker's live ring: a hairline of the state colour around the
   switch, so a generic switch says "on" without a lamp. */
.app__led-ring {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.2;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.switch-anim--active .app__led-ring {
  opacity: 0.9;
  animation: switch-anim-breathe 2.4s ease-in-out infinite;
}

.app__element {
  transform-box: fill-box;
}

/* A rotating part pivots in the drawing's own coordinates around the point its
   inline `transform-origin` names — never `fill-box`, which would swing a
   thumbnail-sized part a whole tile wide. */
.app__drum,
.app__impeller,
.app__blade {
  transform-box: view-box;
}

.app__drum,
.app__impeller,
.app__blade {
  transform-origin: 24px 26.4px;
}

.app__blade {
  transform-origin: 21.4px 30.4px;
}

.app__mower-blade {
  transform-origin: 24px 33px;
}

/* ── Live motion ─────────────────────────────────────────────────────────── */
.switch-anim--active .app__drum,
.switch-anim--active .app__impeller,
.switch-anim--active .app__blade {
  animation: switch-anim-spin 2.6s linear infinite;
}

.switch-anim--active .app__blade {
  animation-duration: 1.1s;
}

.switch-anim--active .app__led {
  animation: switch-anim-breathe 2.4s ease-in-out infinite;
}

.switch-anim--active .app__steam {
  animation: switch-anim-steam 2.2s ease-in-out infinite;
}

.switch-anim--active .app__steam--b,
.switch-anim--active .app__sound--b,
.switch-anim--active .app__flow--b {
  animation-delay: 0.5s;
}

.switch-anim--active .app__drop {
  animation: switch-anim-drip 1.8s ease-in infinite;
}

.switch-anim--active .app__heat path {
  animation: switch-anim-heat 1.6s ease-in-out infinite;
}

.switch-anim--active .app__heat path:nth-child(2) {
  animation-delay: 0.2s;
}

.switch-anim--active .app__heat path:nth-child(3) {
  animation-delay: 0.4s;
}

.switch-anim--active .app__roam {
  animation: switch-anim-roam 5.6s ease-in-out infinite;
}

.switch-anim--active .app__dust circle {
  animation: switch-anim-dust 2.8s ease-out infinite;
}

.switch-anim--active .app__dust circle:nth-child(2) {
  animation-delay: 0.7s;
}

.switch-anim--active .app__dust circle:nth-child(3) {
  animation-delay: 1.4s;
}

.switch-anim--active .app__clippings path {
  animation: switch-anim-clip 1.6s ease-out infinite;
}

.switch-anim--active .app__horn {
  animation: switch-anim-shake 0.9s ease-in-out infinite;
}

.switch-anim--active .app__sound path {
  animation: switch-anim-sound 1.4s ease-in-out infinite;
}

.switch-anim--active .app__sound--c,
.switch-anim--active .app__sound--d {
  animation-delay: 0.25s;
}

.switch-anim--active .app__flow path {
  animation: switch-anim-out 1.5s ease-out infinite;
}

.switch-anim--active .app__current {
  animation: switch-anim-charge 0.9s linear infinite;
}

.switch-anim--active .app__ripple {
  animation: switch-anim-ripple 1.6s ease-out infinite;
}

.switch-anim--active .app__cold-air {
  animation: switch-anim-chill 2.6s ease-in-out infinite;
}

.switch-anim--active .app__cold-air--b {
  animation-delay: 0.5s;
}

.switch-anim--active .app__cold-air--c {
  animation-delay: 1s;
}

/* ── The water valve: pipe, fitting, and a lever that says which way it is ──
   The body is metal — a soft fill with its own outline — and the lever is the
   one part in colour, so the position reads before anything else does. */
.switch-anim__valve-pipe {
  fill: none;
  stroke: color-mix(in srgb, currentColor 34%, transparent);
  stroke-width: 7.4;
}

.switch-anim__valve-wall {
  fill: none;
  stroke: color-mix(in srgb, currentColor 74%, transparent);
  stroke-width: 1.3;
  stroke-linecap: round;
}

/* Water in the bore: a blue column, and a lighter current running up it — the
   gesture says "this valve is letting water through" before the lever is read. */
.switch-anim__valve-water {
  fill: none;
  stroke: #38bdf8;
  stroke-width: 4.2;
}

.switch-anim__valve-flow {
  fill: none;
  stroke: #e0f2fe;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-dasharray: 3 7;
}

.switch-anim--active .switch-anim__valve-flow {
  animation: switch-anim-water 0.9s linear infinite;
}

@keyframes switch-anim-water {
  to {
    stroke-dashoffset: -10;
  }
}

.switch-anim__valve-collar,
.switch-anim__valve-body {
  fill: color-mix(in srgb, currentColor 44%, transparent);
  stroke: color-mix(in srgb, currentColor 78%, rgb(0 0 0));
  stroke-width: 1.2;
}

.switch-anim__valve-seam {
  fill: none;
  stroke: color-mix(in srgb, currentColor 74%, transparent);
  stroke-width: 1;
}

.switch-anim__valve-grip {
  fill: #dc2626;
}

.switch-anim__valve-hub {
  fill: #991b1b;
}

/* Shut: across the valve. Open: a quarter turn down, along the pipe. */
.switch-anim__valve-handle {
  transform-box: view-box;
  transform-origin: 24px 26px;
  transform: rotate(0deg);
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.switch-anim--active .switch-anim__valve-handle {
  transform: rotate(90deg);
}

/* An open valve is a warning — water is running — so the whole fitting breathes
   while it is live, and settles the moment the lever is turned back. */
.switch-anim--active .switch-anim__valve {
  animation: switch-anim-valve-breathe 2.2s ease-in-out infinite;
}

@keyframes switch-anim-valve-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

/* The valve's water is its own statement — dry pipe or running water — so it
   carries its own rule rather than sharing the shelf above. */
.switch-anim__valve-water,
.switch-anim__valve-flow {
  opacity: 0;
  transition: opacity 0.35s ease;
}

.switch-anim--active .switch-anim__valve-water,
.switch-anim--active .switch-anim__valve-flow {
  opacity: 1;
}

/* ── Keyframes ─────────────────────────────────────────────────────────── */

@keyframes switch-anim-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes switch-anim-breathe {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 1;
  }
}

@keyframes switch-anim-ripple {
  0% {
    opacity: 0.7;
    transform: scale(0.5);
  }
  100% {
    opacity: 0;
    transform: scale(1.9);
  }
}

@keyframes switch-anim-steam {
  0% {
    opacity: 0;
    transform: translateY(2px);
  }
  35% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@keyframes switch-anim-drip {
  0%,
  60% {
    opacity: 0;
    transform: translateY(0);
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(4px);
  }
}

@keyframes switch-anim-heat {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}

@keyframes switch-anim-charge {
  to {
    stroke-dashoffset: -7.6;
  }
}

/* The sound leaving the siren: the arcs brighten and fade in place, because a
   pair of arcs that slid sideways would read as drift, not as noise. */
@keyframes switch-anim-sound {
  0%,
  100% {
    opacity: 0.2;
  }
  45% {
    opacity: 1;
  }
}

@keyframes switch-anim-chill {
  0%,
  100% {
    opacity: 0.35;
    transform: translateY(-1.4px);
  }
  50% {
    opacity: 1;
    transform: translateY(1.4px);
  }
}

@keyframes switch-anim-roam {
  0%,
  100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(3px, -2px);
  }
  50% {
    transform: translate(0, 2px);
  }
  75% {
    transform: translate(-3px, -1px);
  }
}

@keyframes switch-anim-dust {
  0% {
    opacity: 0.8;
    transform: translate(0, 0) scale(0.8);
  }
  100% {
    opacity: 0;
    transform: translate(3px, -3px) scale(1.3);
  }
}

@keyframes switch-anim-clip {
  0% {
    opacity: 0;
    transform: translate(0, 0);
  }
  30% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-3px, -5px);
  }
}

@keyframes switch-anim-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(0.6px);
  }
}

@keyframes switch-anim-out {
  0% {
    opacity: 0;
    transform: translateX(-2px);
  }
  40% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateX(2.5px);
  }
}

/* Reduced motion: the drawings stay, the movement stops */
@media (prefers-reduced-motion: reduce) {
  .switch-anim--active * {
    animation: none !important;
  }

  .app__live-marks,
  .app__led,
  .app__tint,
  .app__fill,
  .app__element,
  .app__glow,
  .switch-anim__valve-water,
  .switch-anim__valve-flow {
    opacity: 0.85;
    transition: none;
  }
}
</style>
